import { Card, CardContent, Grid, VStack, HStack } from '@components/base/layout';
import { Text } from '@components/base/display';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

const gapOptions = [0, 1, 2, 4, 6, 8] as const;

const policyCards = [
  { name: 'Auto Insurance', id: 'POL-2026-001', premium: '$142/mo' },
  { name: 'Home Insurance', id: 'POL-2026-002', premium: '$89/mo' },
  { name: 'Life Insurance', id: 'POL-2026-003', premium: '$45/mo' },
  { name: 'Health Insurance', id: 'POL-2026-004', premium: '$320/mo' },
  { name: 'Umbrella Policy', id: 'POL-2026-005', premium: '$25/mo' },
  { name: 'Renters Insurance', id: 'POL-2026-006', premium: '$18/mo' },
] as const;

function GridCell({ children, className = '' }: Readonly<{ children: React.ReactNode; className?: string }>) {
  return (
    <div className={`bg-[#E8E6E3] p-3 rounded text-sm text-center ${className}`}>
      {children}
    </div>
  );
}

function GridShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="grid">
          <Grid
            columns={Number(playgroundProps.columns) as 1 | 2 | 3 | 4 | 6 | 12}
            gap={Number(playgroundProps.gap) as 0 | 1 | 2 | 4 | 6 | 8}
          >
            <GridCell>1</GridCell>
            <GridCell>2</GridCell>
            <GridCell>3</GridCell>
            <GridCell>4</GridCell>
            <GridCell>5</GridCell>
            <GridCell>6</GridCell>
          </Grid>
      </ShowcasePreviewSection>

      {/* Column Configurations */}
      <ShowcaseDemoSection
        title="Column Configurations"
        description="Grid supports 1-12 columns for flexible layouts"
       
      >
          <VStack gap={6}>
            {[2, 3, 4, 6].map((cols) => (
              <VStack key={cols} gap={1}>
                <Text size="xs" color="muted">{cols} columns</Text>
                <Grid columns={cols} gap={2}>
                  {Array.from({ length: cols }, (_, i) => (
                    <GridCell key={`col-${cols}-cell-${i + 1}`}>{i + 1}</GridCell>
                  ))}
                </Grid>
              </VStack>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* Gap Options */}
      <ShowcaseDemoSection
        title="Gap Options"
        description="Control spacing between grid items (0-8 maps to 0-32px)"
       
      >
          <VStack gap={4}>
            {gapOptions.filter(g => g > 0).map((gap) => (
              <VStack key={gap} gap={1}>
                <Text size="xs" color="muted">gap={gap} ({gap * 4}px)</Text>
                <Grid columns={4} gap={gap}>
                  <GridCell>A</GridCell>
                  <GridCell>B</GridCell>
                  <GridCell>C</GridCell>
                  <GridCell>D</GridCell>
                </Grid>
              </VStack>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* Responsive Examples */}
      <ShowcaseDemoSection
        title="Responsive Grid"
        description="Grids can adapt to different screen sizes using responsive column configurations"
       
      >
          <VStack gap={4}>
            <Text size="sm" color="muted">
              Resize the browser to see the grid adapt: 1 column on mobile, 2 on tablet, 4 on desktop
            </Text>
            <Grid columns={{ base: 1, sm: 2, lg: 4 }} gap={4}>
              <GridCell>Policy 1</GridCell>
              <GridCell>Policy 2</GridCell>
              <GridCell>Policy 3</GridCell>
              <GridCell>Policy 4</GridCell>
            </Grid>
          </VStack>
      </ShowcaseDemoSection>

      {/* Grid Item Spanning */}
      <ShowcaseDemoSection
        title="Grid Item Spanning"
        description="Grid items can span multiple columns using the span prop"
       
      >
          <VStack gap={4}>
            <VStack gap={1}>
              <Text size="xs" color="muted">12-column grid with spanning items</Text>
              <Grid columns={12} gap={2}>
                <Grid.Item span={12}>
                  <GridCell>Header (span 12)</GridCell>
                </Grid.Item>
                <Grid.Item span={3}>
                  <GridCell>Sidebar (span 3)</GridCell>
                </Grid.Item>
                <Grid.Item span={9}>
                  <GridCell>Main Content (span 9)</GridCell>
                </Grid.Item>
                <Grid.Item span={4}>
                  <GridCell>Col 4</GridCell>
                </Grid.Item>
                <Grid.Item span={4}>
                  <GridCell>Col 4</GridCell>
                </Grid.Item>
                <Grid.Item span={4}>
                  <GridCell>Col 4</GridCell>
                </Grid.Item>
                <Grid.Item span={6}>
                  <GridCell>Half (span 6)</GridCell>
                </Grid.Item>
                <Grid.Item span={6}>
                  <GridCell>Half (span 6)</GridCell>
                </Grid.Item>
              </Grid>
            </VStack>
          </VStack>
      </ShowcaseDemoSection>

      {/* Real-World Card Grid */}
      <ShowcaseDemoSection
        title="Real-World Card Grid"
        description="Common pattern: responsive card grid for displaying policies or claims"
       
      >
          <Grid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
            {policyCards.map((policy) => (
              <Card key={policy.id}>
                <CardContent>
                  <VStack gap={2}>
                    <Text size="sm" weight="semibold">{policy.name}</Text>
                    <Text size="xs" color="muted">Policy #: {policy.id}</Text>
                    <HStack justify="between">
                      <Text size="xs" color="muted">Premium</Text>
                      <Text size="sm" weight="semibold">{policy.premium}</Text>
                    </HStack>
                  </VStack>
                </CardContent>
              </Card>
            ))}
          </Grid>
      </ShowcaseDemoSection>

      {/* Dashboard Layout */}
      <ShowcaseDemoSection
        title="Dashboard Layout"
        description="Complex layouts using 12-column grid with various span configurations"
       
      >
          <Grid columns={12} gap={4}>
            <Grid.Item span={{ base: 12, lg: 8 }}>
              <Card className="h-40">
                <CardContent>
                  <Text size="sm" weight="semibold">Claims Overview Chart</Text>
                  <Text size="xs" color="muted">Main content area (8 columns on desktop)</Text>
                </CardContent>
              </Card>
            </Grid.Item>
            <Grid.Item span={{ base: 12, lg: 4 }}>
              <Card className="h-40">
                <CardContent>
                  <Text size="sm" weight="semibold">Quick Stats</Text>
                  <Text size="xs" color="muted">Sidebar (4 columns on desktop)</Text>
                </CardContent>
              </Card>
            </Grid.Item>
            <Grid.Item span={{ base: 12, md: 6, lg: 3 }}>
              <Card>
                <CardContent>
                  <Text size="sm" weight="semibold">Active Policies</Text>
                  <Text size="2xl" weight="bold">12</Text>
                </CardContent>
              </Card>
            </Grid.Item>
            <Grid.Item span={{ base: 12, md: 6, lg: 3 }}>
              <Card>
                <CardContent>
                  <Text size="sm" weight="semibold">Open Claims</Text>
                  <Text size="2xl" weight="bold">3</Text>
                </CardContent>
              </Card>
            </Grid.Item>
            <Grid.Item span={{ base: 12, md: 6, lg: 3 }}>
              <Card>
                <CardContent>
                  <Text size="sm" weight="semibold">Total Premium</Text>
                  <Text size="2xl" weight="bold">$639</Text>
                </CardContent>
              </Card>
            </Grid.Item>
            <Grid.Item span={{ base: 12, md: 6, lg: 3 }}>
              <Card>
                <CardContent>
                  <Text size="sm" weight="semibold">Documents</Text>
                  <Text size="2xl" weight="bold">24</Text>
                </CardContent>
              </Card>
            </Grid.Item>
          </Grid>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const GridShowcase: ShowcaseDefinition = {
  id: 'grid',
  name: 'Grid',
  category: 'layout',
  description: 'CSS Grid-based layout for multi-column arrangements',
  propsDefinition: [
    {
      name: 'columns',
      type: 'select',
      options: ['1', '2', '3', '4', '6', '12'],
      defaultValue: '3',
      description: 'Number of grid columns',
    },
    {
      name: 'gap',
      type: 'select',
      options: ['0', '1', '2', '4', '6', '8'],
      defaultValue: '4',
      description: 'Gap between grid items (multiplied by 4px)',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'Uses semantic <div> element',
      'Grid items maintain DOM order',
    ],
    keyboardNav: [
      'Tab: Navigate through focusable grid items',
    ],
    notes: [
      'Ensure logical reading order matches visual order',
      'Use responsive columns for better mobile experience',
      'Grid items are accessible in document flow order',
    ],
  },
  component: GridShowcaseComponent,
};
